import { Component, OnInit, OnDestroy, ViewChild, ElementRef, HostListener, ViewEncapsulation } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { AuthenticationService } from '../../core/services/authentication.service';
import { environment } from 'src/environments/environment.production';
import { AppConfig } from 'src/app/app.config';
import { UserRole } from 'src/core/helpers/user-roles';
import { SentryService } from 'src/core/services/sentry.service';
import { RightsholderBoardService } from 'src/core/services/rightsholder-board.service';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { RightsHolder } from 'src/core/Models/interfaces.model';
import { OrganizationService } from 'src/core/services/organization.service';
import { isAllowedDomainEmail } from 'src/shared/guards/email-domain.guard';

@Component({
  selector: 'app-main-header',
  templateUrl: './main-header.component.html',
  encapsulation: ViewEncapsulation.ShadowDom,
  styleUrls: ['./main-header.component.scss']
})
export class MainHeaderComponent implements OnInit, OnDestroy {
  @ViewChild('labelSearchInput') labelSearchInput!: ElementRef<HTMLInputElement>;

  Name!: string;
  currentUser: any;
  currentUserImage: string = '';
  IsLoggedIn: boolean = false;
  showReport: boolean = false;
  showDispute: boolean = false;
  showAdminTools: boolean = false;
  showUserManagement: boolean = false;
  showLabelManagement: boolean = false;
  showFinance: boolean = false;
  showDistributions: boolean = false;
  showDataMerge: boolean = false;
  showAuditDashboard: boolean = false;
  showCacheSync: boolean = false;
  showSearchAndAssign: boolean = false;
  showSwitchLabel: boolean = false;
  showNewFinance: boolean = false;
  enableSentry: boolean = AppConfig.settings.enableSentry;
  protected isFinanceActiveMPO = AppConfig.settings.isFinanceActiveMPO;
  protected isFinanceActiveMNN = AppConfig.settings.isFinanceActiveMNN;
  currentUserRole: any;

  // New properties for custom navbar
  isDropdownOpen: boolean = false;

  get displayLabel(): string {
    return this.currentUser?.board?.roles?.[0] === UserRole.Superadmin
      ? 'Superadmin'
      : this.currentUser?.board?.title || '';
  }
  isMobileMenuOpen: boolean = false;
  isSwitchLabelModalOpen: boolean = false;

  // Search properties
  searchQuery: string = '';
  searchResults: any[] = [];
  showInitialMessage: boolean = true;
  showNoResultMessage: boolean = false;
  showSearchResults: boolean = false;
  showViewAllButton: boolean = true;
  private searchSubject = new Subject<string>();

  currentOrganizationLogo: string = 'assets/images/logo_mpo.svg';

  protected apiServer = AppConfig.settings.apiServer;

  constructor(
    private router: Router,
    public authService: AuthenticationService,
    private translate: TranslateService,
    private sentryService: SentryService,
    private rightsholderBoardService: RightsholderBoardService,
    private organizationService: OrganizationService
  ) {
    this.authService.isLoggedIn.subscribe((res: boolean) => {
      this.IsLoggedIn = res;
    });
    if (sessionStorage.getItem('organisation') == 'no') {
      this.currentOrganizationLogo = 'assets/images/no/logo/logo.svg';
    }
    this.currentUser = JSON.parse(localStorage.getItem('currentUser')!);

    this.translate.setDefaultLang(environment.language1);
    this.translate.use(environment.language1);
    if (this.currentUser != null || '') {
      this.Name =
        this.currentUser.user.firstName + ' ' + this.currentUser.user.lastName;

      if (this.currentUser.user.hasImage)
        this.currentUserImage =
          this.apiServer.awsS3URL +
          `/users/` +
          this.currentUser.user.id +
          `/static/image-190x190.jpg`;
    }
  }
  logout() {
    this.authService.logout();
  }

  adjustTabsBasedOnRoles() {
    let currentUserRole = {
      id: this.currentUser.board.roles[0],
    };

    const currentUserEmail: string | null = this.currentUser?.user?.email ?? null;
    const canSeeCrossworkersModules = isAllowedDomainEmail(currentUserEmail);
    const canSeeNewFinanceModules = isAllowedDomainEmail(currentUserEmail);

    this.showNewFinance =  currentUserRole.id !== UserRole.Superadmin;

    this.showAdminTools = [
      UserRole.Superadmin,
      UserRole.LabelAgent,
    ].includes(currentUserRole.id);
    this.showLabelManagement = [
      UserRole.Superadmin,
      UserRole.LabelAdmin,
      UserRole.LabelUser,
      UserRole.LabelAgent,
    ].includes(currentUserRole.id);
    this.showUserManagement = [
      UserRole.Superadmin,
      UserRole.LabelAdmin,
    ].includes(currentUserRole.id);

    this.showSearchAndAssign = [
      UserRole.Superadmin,
      UserRole.LabelAdmin,
      UserRole.LabelUser,
      UserRole.LabelAgent
    ].includes(currentUserRole.id);

    this.showSwitchLabel = [
      UserRole.LabelAdmin,
      UserRole.LabelUser
    ].includes(currentUserRole.id);

    this.showReport = [
      UserRole.Superadmin,
      UserRole.LabelAdmin,
      UserRole.LabelUser,
      UserRole.LabelAgent,
    ].includes(currentUserRole.id);

    this.showDispute = [
      UserRole.Superadmin,
      UserRole.LabelAdmin,
      UserRole.LabelUser,
      UserRole.LabelAgent,
    ].includes(currentUserRole.id);
    if (sessionStorage.getItem('organisation') == 'no' && this.isFinanceActiveMNN) {
      this.showFinance = [
        UserRole.LabelAdmin,
        UserRole.LabelUser,
        UserRole.LabelAgent,
      ].includes(currentUserRole.id);
    }
    if (sessionStorage.getItem('organisation') == 'dk' && this.isFinanceActiveMPO) {
      this.showFinance = [
        UserRole.LabelAdmin,
        UserRole.LabelUser,
        UserRole.LabelAgent,
      ].includes(currentUserRole.id);
    }
    this.showDistributions = [UserRole.Superadmin].includes(currentUserRole.id);

    this.showDataMerge = [UserRole.Superadmin].includes(currentUserRole.id);
    this.showAuditDashboard = [UserRole.Superadmin].includes(currentUserRole.id) && canSeeCrossworkersModules;
    this.showCacheSync = [UserRole.Superadmin].includes(currentUserRole.id) && canSeeCrossworkersModules;
  }

  ngOnInit() {
    this.currentUserRole = {
      id: this.currentUser.board.roles[0],
    };
    this.adjustTabsBasedOnRoles();

    // Set up search debounce
    this.searchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe(searchQuery => {
      this.performSearch(searchQuery);
    });

    // check if currentUser accept cookies or not
    if (localStorage.getItem('currentUser') != null) {
      this.currentUser = JSON.parse(localStorage.getItem('currentUser')!);
      if (this.currentUser.userCookiesDetails?.isUserCookiesAccepted &&
        this.enableSentry &&
        !this.sentryService.isSentryInitialized()) {

        this.sentryService.initializeSentry();

        // Set user context
        this.sentryService.setUser({
          id: this.currentUser.user.id,
          email: this.currentUser.user.email,
          username: `${this.currentUser.user.firstName} ${this.currentUser.user.lastName}`,
          board: `${this.currentUser.board.title}`
        });
      }
    }
  }

  /**
   * Clean up event listeners
   */
  ngOnDestroy(): void {
    this.searchSubject.complete();
  }

  /**
   * Toggle dropdown menu
   */
  toggleDropdown(): void {
    this.isDropdownOpen = !this.isDropdownOpen;
  }

  /**
   * Close dropdown menu
   */
  closeDropdown(): void {
    this.isDropdownOpen = false;
  }

  /**
   * Toggle mobile menu
   */
  toggleMobileMenu(): void {
    this.isMobileMenuOpen = !this.isMobileMenuOpen;
  }

  /**
   * Open switch label modal
   */
  openSwitchLabelModal(): void {
    this.isSwitchLabelModalOpen = true;
    this.closeDropdown();

    // Focus the input after the modal is rendered
    setTimeout(() => {
      if (this.labelSearchInput) {
        this.labelSearchInput.nativeElement.focus();
      }
    }, 100);
  }

  /**
   * Close switch label modal
   */
  closeSwitchLabelModal(): void {
    this.isSwitchLabelModalOpen = false;
    this.resetSearch();
  }

  /**
   * Handle search input changes
   */
  onSearchChange(): void {
    if (this.searchQuery.trim().length === 0) {
      // Show initial message when search is empty
      this.showInitialMessage = true;
      this.showNoResultMessage = false;
      this.showSearchResults = false;
      this.showViewAllButton = true;
      this.searchResults = [];
    } else {
      // Hide initial message when user starts typing
      this.showInitialMessage = false;
      this.searchSubject.next(this.searchQuery);
    }
  }

  onAvatarError(event: Event): void {
    const img = event.target as HTMLImageElement;
    if (img) {
      img.style.display = 'none';
    }
  }

  private performSearch(searchQuery: string): void {
    const organizationId = sessionStorage.getItem('organisation') === 'no' ? 2 : 1;

    this.rightsholderBoardService.getRightsholders(organizationId, 0, 10, searchQuery)
      .subscribe({
        next: (response) => {
          if (response.result && response.data) {
            this.searchResults = response.data.map(item => ({
              name: item.rightsholderName,
              boardId: item.boardId,
              rightsholderId: item.rightsholderId
            }));

            if (this.searchResults.length > 0) {
              this.showSearchResults = true;
              this.showNoResultMessage = false;
              this.showViewAllButton = false;
            } else {
              this.showSearchResults = false;
              this.showNoResultMessage = true;
              this.showViewAllButton = false;
            }
          }
        },
        error: (error) => {
          console.error('Error searching labels:', error);
          this.searchResults = [];
          this.showSearchResults = false;
          this.showNoResultMessage = true;
          this.showViewAllButton = false;
        }
      });
  }

  private resetSearch(): void {
    this.searchQuery = '';
    this.searchResults = [];
    this.showInitialMessage = true;
    this.showNoResultMessage = false;
    this.showSearchResults = false;
    this.showViewAllButton = true;
  }

  switchLabel(boardId: any) {
    if (UserRole.LabelAdmin == this.currentUserRole.id || UserRole.LabelAgent == this.currentUserRole.id || UserRole.LabelUser == this.currentUserRole.id) {
      this.authService.getCurrentBoard(boardId, JSON.parse(
        localStorage.getItem('currentUser')!
      ).authenticated.access_token)
        .subscribe((currentBoard: any) => {
          this.currentUser = {
            ...this.currentUser,
            "board": currentBoard
          };
          localStorage.setItem("currentUser", JSON.stringify(this.currentUser));
          this.authService
            .getCurrentRightsHolderbyBoardId(
              this.currentUser.board.id,
              this.currentUser.authenticated.access_token
            )
            .subscribe((currentRightsHolder: RightsHolder) => {
              this.currentUser = {
                ...this.currentUser,
                rightsHolder: currentRightsHolder
              };
              localStorage.setItem('currentUser', JSON.stringify(this.currentUser));
              this.organizationService.setOrganizationIdInStorage(this.currentUser);
              this.closeSwitchLabelModal();
            });
        });
    }
  }


  @HostListener('document:keydown.escape')
  handleEscapeKey(): void {
    if (this.isSwitchLabelModalOpen) {
      this.closeSwitchLabelModal();
    }
    if (this.isDropdownOpen) {
      this.closeDropdown();
    }
  }

  @HostListener('document:click', ['$event'])
  handleClickOutside(event: Event): void {
    const target = event.target as HTMLElement;
    const dropdown = target.closest('.dropdown');

    if (this.isDropdownOpen && !dropdown) {
      this.closeDropdown();
    }
  }

}
